/**
 * Centralized regex pattern constants for DesignStudioFrontEnd
 * Use these constants instead of defining regex patterns inline throughout the codebase
 */
export class RegexPatternConstants {
  // ==================== NAME VALIDATION PATTERNS ====================

  /**
   * Alphanumeric name pattern - starts with letter, allows letters, numbers, and spaces (no tabs)
   * Used for: page names, screen names, workflow names, custom component names
   * Example valid: "My Page 1", "TestName"
   * Note: Do NOT use 'g' flag - it causes stateful lastIndex behavior with test()
   */
  public static readonly alphanumericName = /^[a-zA-Z]+[a-zA-Z0-9 ]*[a-zA-Z0-9]*$/; // NOSONAR

  /**
   * Alphanumeric name with underscore - starts with letter, allows letters, numbers, underscores, and spaces (no tabs)
   * Used for: screen creation, page validation
   * Example valid: "My_Page_1", "Test_Name"
   */
  public static readonly alphanumericNameWithUnderscore = /^[a-zA-Z]+[a-zA-Z0-9_ ]*[a-zA-Z0-9]*$/; // NOSONAR

  /**
   * BigQuery dataset name pattern,  Consumed rest name pattern - starts with letter, allows letters, numbers, underscores
   * Used for: BigQuery dataset name and consumed rest component name validation in technology stack
   * Example valid: "my_dataset", "Dataset123"
   * Note: Do NOT use 'g' flag - it causes stateful lastIndex behavior with test()
   */
  public static readonly alphanumericWithoutSpace = /^[a-zA-Z]+[a-zA-Z0-9_]*[a-zA-Z0-9]*$/; // NOSONAR

  /**
   * Email pattern for technology stack validation
   * Used for: technology stack email configuration
   */
  public static readonly emailTechStack = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i; // NOSONAR

  /**
   * Environment name validation:
   * - Must start with a letter
   * - Can contain letters, digits, hyphens, and underscores
   * - Must be 2-50 characters long
   * - No spaces or special characters allowed
   */
  public static readonly environmentName = /^[a-zA-Z][a-zA-Z0-9_-]{1,49}$/; // NOSONAR

  /**
   * Safe text pattern - rejects HTML tags (< >) and SQL injection characters (; --)
   * Used for: description fields, free-text inputs
   */
  public static readonly safeText = /^(?!.*--)(?!.*\/\*)(?!.*\*\/)[^<>;]*$/; // NOSONAR

   /**
   *  ($ or $$ or $${ or $${word)
   *  User for: Query String and Query View sql validation. $ is not allowed as part of Query
   */
  public static readonly dollarMatch = /(\${1,2})(\{?)([^}\s]*)$/; // NOSONAR

  // ==================== FILE PATH PATTERNS ====================

  /**
   * TypeScript/JavaScript file path pattern
   * Used for: file path validation in settings
   * Example valid: "/path/to/file.ts", "script.js"
   */
  public static readonly tsJsFilePath = /^(\/?[a-z_\-\s0-9.]+)+\.(ts|js)$/; // NOSONAR

  /**
   * CSS/SCSS file path pattern
   * Used for: stylesheet path validation
   * Example valid: "/styles/main.css", "theme.scss"
   */
  public static readonly cssScssFilePath = /^(\/?[a-z_\-\s0-9.]+)+\.(css|scss)$/; // NOSONAR

  // ==================== LINK NAME PATTERNS ====================

  /**
   * Link name pattern - starts with letter, allows letters, numbers, spaces, and hyphens
   * Used for: file attachments link name validation
   * Example valid: "My Link", "link-name-1"
   */
  public static readonly linkName = /^[A-Za-z][A-Za-z0-9\s-]*$/; // NOSONAR

  /**
   * General URL pattern - matches various URL formats
   * Used for: file attachments link URL validation
   * Example valid: "https://www.example.com", "http://example.com/path"
   */
  public static readonly generalUrl = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/; // NOSONAR

  /**
   * Repository URL pattern - matches HTTP/HTTPS URLs with optional www prefix
   * Used for: Git repository server URL validation in organization management
   * Example valid: "https://github.com", "http://gitlab.example.com/path"
   */
  public static readonly repositoryUrl = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/; // NOSONAR

  /**
   * GitHub organization name pattern - alphanumeric with optional hyphen-separated segments
   * Used for: GitHub organization name validation in organization management
   * Example valid: "my-org", "MyOrg123", "org-name-here"
   */
  public static readonly githubOrganizationName = /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/; // NOSONAR

  // ==================== USER VALIDATION PATTERNS ====================

  /**
   * User name pattern - allows alphanumeric with spaces, must have at least one non-space character
   * Used for: user first name, last name validation
   * Example valid: "John", "John Doe", "User123"
   */
  public static readonly userName = /^[A-Za-z0-9]*[A-Za-z0-9\s]+[A-Za-z0-9]*$/; // NOSONAR

  /**
   * User email pattern - standard email format with domain length validation
   * Used for: user email validation in users component
   * Example valid: "user@example.com", "user.name@domain.org"
   */
  public static readonly userEmail = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$/; // NOSONAR

  // ==================== IDENTIFIER PATTERNS ====================

  /**
   * Identifier pattern - starts with lowercase letter, allows lowercase letters, numbers, and underscores
   * Must not end with an underscore
   * Used for: screen node identifier validation
   * Example valid: "my_identifier", "button1", "tab_item_2"
   * Example invalid: "MyIdentifier", "1button", "test_"
   */
  public static readonly identifier = /^[a-z]([a-z0-9_]*[a-z0-9])?$/; // NOSONAR

  /**
   * Style class pattern - starts with letter, allows letters, numbers, hyphens, and underscores
   * Used for: CSS class name validation
   * Example valid: "my-class", "Button_Style", "class-name-1"
   * Example invalid: "1class", "-class"
   */
  public static readonly styleClass = /^[a-zA-Z][a-zA-Z0-9_-]*$/; // NOSONAR

}
